


import React, { useState } from 'react'

import { getErrorMessage, getFileExtension } from 'utils'

import { useSnackbar } from 'contexts'

import { useTheme, Stack, CircularProgress, Typography, Box, Tooltip, IconButton } from '@mui/material'

import { Button } from '@repo/ui'
import { Modal } from '@repo/ui'
import { Alert } from '@repo/ui'
import { FileUploadField } from 'components'
import { DownloadIcon, InfoIcon } from 'lucide-react'
import { createApiCall } from 'services'


interface ImportModalProps {
  open: boolean
  source: string
  onImport: any
  onClose: () => void
  refetch: () => void
}

interface ImportResult {
  totalProcessed: number
  successCount: number
  failureCount: number
  results: Array<{
    row: number
    // customers report `email`; products and sales report `identifier`
    // (sku/title, or the buyer email)
    email?: string
    identifier?: string
    success: boolean
    error?: string
  }>
}

// VERA-465 — route the template + upload calls to the endpoint that matches the
// surface. Owners are customers (existing endpoints); products get their own.
const IMPORT_ENDPOINTS: Record<string, { template: string; upload: string }> = {
  products: {
    template: '/products/admin/bulk-create/template',
    upload: '/products/admin/bulk-create/upload',
  },
  owners: {
    template: '/customers/admin/bulk-create/template',
    upload: '/customers/admin/bulk-create/upload',
  },
  // VRG-455 — record sales in bulk. Routed through the same registry-driven
  // template + parser as the two above (§30), so the template cannot advertise
  // a column the upload ignores.
  //
  // ⚠️ This source is ALL-OR-NOTHING, unlike owners and products. The API
  // validates every row and writes NOTHING unless all of them pass, because a
  // duplicate pending-ownership record is not inert — each one is a live
  // claimable entitlement, so the obvious recovery from a partial success
  // (re-upload the fixed file) double-records the rows that landed. The
  // response still names EVERY failing row, so a 400-line file is fixable in
  // one pass rather than one upload per mistake.
  sales: {
    template: '/pending-ownership/admin/bulk-create/template',
    upload: '/pending-ownership/admin/bulk-create/upload',
  },
}

export const ImportModal = (props: ImportModalProps) => {
  const { open, onClose = () => {}, onImport = () => {}, source, refetch } = props
  const theme = useTheme()

  const endpoints = IMPORT_ENDPOINTS[source] ?? IMPORT_ENDPOINTS.owners

  const [files, setFiles] = useState<any[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)

  const { open: notification } = useSnackbar()

  const handleDownloadTemplate = async () => {
    try {
      const response = await createApiCall({
        method: 'GET',
        url: endpoints.template,
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      // e.g. vera-owners-import-template.csv / vera-products-import-template.csv
      link.setAttribute('download', `vera-${source}-import-template.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      notification({
        type: 'success',
        title: 'Template downloaded successfully',
      })
    } catch (error) {
      notification({
        type: 'error',
        title: 'Failed to download template',
      })
    }
  }

  const handleImport = async () => {
    if (files.length === 0) {
      notification({
        type: 'error',
        title: 'Please attach a file to import',
      })
      return
    }

    setIsUploading(true)
    setImportResult(null)

    try {
      const formData = new FormData()
      formData.append('file', files[0].file)

      const response = await createApiCall({
        method: 'POST',
        url: endpoints.upload,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      setImportResult(response.data)

      if (response.data.failureCount === 0) {
        refetch()
        onImport()
        // Close modal after successful upload
        setTimeout(() => {
          handleClose()
        }, 500)
      } else {
        notification({
          type: 'warning',
          title: `Upload completed with errors`,
          text: `${response.data.successCount} ${source} uploaded successfully, ${response.data.failureCount} failed. Review the errors below.`,
        })
      }
    } catch (error) {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: `Upload failed`,
        text: `Unable to upload ${source}: ${message}. Please check your file format and try again.`,
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    onClose()
    setFiles([])
    setImportResult(null)
  }

  const isFileUploadFielded = files.length > 0

  return (
    <Modal
      title={`Import ${source}`}
      open={open}
      onClose={handleClose}
      hideBackdrop={true}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            {importResult ? 'Close' : 'Back'}
          </Button>
          {!importResult && (
            <Button
              onClick={handleImport}
              disabled={!isFileUploadFielded || isUploading}
            >
              {isUploading ? 'Importing...' : 'Import'}
            </Button>
          )}
        </>
      }
    >
      <Stack spacing={4}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body3" color="text.secondary">
              Download the CSV template to get started
            </Typography>
            <Tooltip
              title={`Download the CSV template with the expected columns for importing ${source}. Fill in the required fields, then upload the file.`}
              placement="top"
            >
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <InfoIcon size={18} color={theme.palette.primary[500]} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Button
            variant="secondary"
            startIcon={<DownloadIcon size={16} strokeWidth={1} />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
        </Stack>

        {/* CSV-only: the backend bulk-create/upload endpoint parses CSV only
            (customers.admin.controller -> csvParserService.parseCustomersCsv).
            TODO(VERA-914: Owner bulk import — backend xlsx parsing): re-add
            .xls/.xlsx accept here once the API parses Excel, so the affordance
            matches reality. */}
        <FileUploadField
          acceptFiles={{
            'text/csv': ['.csv'],
          }}
          description="Accepts: CSV (up to 10,000 rows)"
          onFilesChange={setFiles}
        />

        {isUploading && (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Uploading and processing...</Typography>
          </Box>
        )}

        {importResult && (
          <Stack spacing={2}>
            <Alert severity={importResult.failureCount === 0 ? 'success' : 'warning'}>
              <Typography variant="body2" fontWeight={600}>
                Import Summary
              </Typography>
              <Typography variant="body3">
                Total Processed: {importResult.totalProcessed}
              </Typography>
              <Typography variant="body3" color="success.main">
                Successful: {importResult.successCount}
              </Typography>
              {importResult.failureCount > 0 && (
                <Typography variant="body3" color="error.main">
                  Failed: {importResult.failureCount}
                </Typography>
              )}
            </Alert>

            {importResult.failureCount > 0 && (
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                <Typography variant="body3" fontWeight={600} mb={1}>
                  Errors:
                </Typography>
                {importResult.results
                  .filter(r => !r.success)
                  .slice(0, 10)
                  .map(result => (
                    <Typography key={result.row} variant="body3" color="error.main">
                      Row {result.row} ({result.identifier ?? result.email}): {result.error}
                    </Typography>
                  ))}
                {importResult.results.filter(r => !r.success).length > 10 && (
                  <Typography variant="body3" color="text.secondary">
                    ... and {importResult.results.filter(r => !r.success).length - 10} more errors
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
